/*
  Shared Teams — the browser client (ADR-0082).

  A team is a reusable collection of PEOPLE. Nothing here grants a permission:
  team `owner` is authority over one roster, never a tenant role, and joining a
  team changes nobody's access.

  THE ONE THING THIS MODULE MUST NOT LET A SCREEN DO is show a pending person as
  a member. `viewerRole` and `viewerPendingKind` are separate fields for exactly
  that reason, and `memberCount` counts ACCEPTED members only — the same set a
  LAMA campaign would expand to.
*/

import { AdminApiError } from "./admin-users-api.js";
import { csrfHeaders } from "../auth/session-api.js";

const BASE = "/api/auth/teams";
const ADMIN_BASE = "/api/auth/admin/teams";

export type TeamMemberRole = "owner" | "member";
export type TeamRequestKind = "invitation" | "join_request";
export type TeamRequestStatus =
  | "pending"
  | "accepted"
  | "declined"
  | "cancelled";

export interface Team {
  teamId: string;
  name: string;
  description: string | null;
  /** Optimistic-concurrency token. Send it back on every owner mutation. */
  version: number;
  archivedAt: string | null;
  createdAt: string;
  /** ACCEPTED members who could still be sent work. A pending invitation is
   *  not counted — they have not agreed to be. */
  memberCount: number;
  ownerCount: number;
  /** The VIEWER's own accepted role, or null. NEVER inferred from the counts. */
  viewerRole: TeamMemberRole | null;
  viewerPendingKind: TeamRequestKind | null;
}

export interface TeamPage {
  items: Team[];
  nextCursor: string | null;
  /** Teams matching the filter, not just this page. */
  totalCount: number;
}

export interface TeamAdminSummary {
  activeTeams: number;
  archivedTeams: number;
  acceptedMemberships: number;
}

export interface TeamMember {
  userId: string;
  displayName: string | null;
  primaryEmail: string | null;
  role: TeamMemberRole;
  joinedVia:
    | "created"
    | "invitation"
    | "join_request"
    | "admin_recovery"
    | "seed";
  joinedAt: string;
  /** Whether this person could still be sent work. SHOWN rather than filtered:
   *  an owner needs to see that somebody is on the roster and unreachable. */
  deliverable: boolean;
}

export interface TeamMemberPage {
  items: TeamMember[];
  nextCursor: string | null;
}

export interface TeamRequest {
  requestId: string;
  teamId: string;
  teamName: string;
  teamArchived: boolean;
  /** The SUBJECT — the person who would become a member. Never the actor. */
  userId: string;
  userDisplayName: string | null;
  kind: TeamRequestKind;
  status: TeamRequestStatus;
  initiatedByUserId: string;
  initiatedByDisplayName: string | null;
  resolvedByUserId: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export interface TeamRequestPage {
  items: TeamRequest[];
  nextCursor: string | null;
}

export type TeamRequestAction = "accept" | "decline" | "cancel";

export interface TeamRequestOutcome {
  outcome:
    | "invitation_accepted"
    | "invitation_declined"
    | "invitation_withdrawn"
    | "join_request_approved"
    | "join_request_declined"
    | "join_request_cancelled"
    | "already_resolved";
}

export interface TeamJoinOutcome {
  /** `joined` means an opposite-direction request was already pending, so this
   *  action RESOLVED it rather than creating a second row. */
  outcome: "invited" | "requested" | "joined";
  requestId: string;
}

export async function listTeams(options: {
  scope: "mine" | "explore";
  query?: string;
  cursor?: string | null;
  limit?: number;
}): Promise<TeamPage> {
  const params = new URLSearchParams({ scope: options.scope });
  if (options.query) params.set("q", options.query);
  if (options.cursor) params.set("cursor", options.cursor);
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  return (await request(`${BASE}?${params.toString()}`)) as TeamPage;
}

export async function listAdminTeams(
  options: {
    query?: string;
    cursor?: string | null;
    includeArchived?: boolean;
    limit?: number;
  } = {},
): Promise<TeamPage> {
  const params = new URLSearchParams();
  if (options.query) params.set("q", options.query);
  if (options.cursor) params.set("cursor", options.cursor);
  if (options.includeArchived) params.set("includeArchived", "true");
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  const query = params.toString();
  return (await request(
    query ? `${ADMIN_BASE}?${query}` : ADMIN_BASE,
  )) as TeamPage;
}

export async function getTeamAdminSummary(): Promise<TeamAdminSummary> {
  return (await request(`${ADMIN_BASE}/summary`)) as TeamAdminSummary;
}

export async function getTeam(teamId: string): Promise<Team> {
  return (await request(`${BASE}/${encodeURIComponent(teamId)}`)) as Team;
}

export async function listTeamMembers(
  teamId: string,
  options: { cursor?: string | null } = {},
): Promise<TeamMemberPage> {
  const params = new URLSearchParams();
  if (options.cursor) params.set("cursor", options.cursor);
  const query = params.toString();
  const path = `${BASE}/${encodeURIComponent(teamId)}/members`;
  return (await request(query ? `${path}?${query}` : path)) as TeamMemberPage;
}

export async function createTeam(input: {
  name: string;
  description: string | null;
}): Promise<{ teamId: string; version: number }> {
  return (await request(BASE, {
    method: "POST",
    body: JSON.stringify(input),
  })) as { teamId: string; version: number };
}

export async function updateTeam(
  teamId: string,
  input: { name: string; description: string | null; version: number },
): Promise<{ version: number }> {
  return (await request(`${BASE}/${encodeURIComponent(teamId)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  })) as { version: number };
}

export async function archiveTeam(
  teamId: string,
  version: number,
): Promise<{ version: number; cancelledRequests: number }> {
  return (await request(`${BASE}/${encodeURIComponent(teamId)}/archive`, {
    method: "POST",
    body: JSON.stringify({ version }),
  })) as { version: number; cancelledRequests: number };
}

export async function inviteToTeam(
  teamId: string,
  userId: string,
): Promise<TeamJoinOutcome> {
  return (await request(`${BASE}/${encodeURIComponent(teamId)}/invitations`, {
    method: "POST",
    body: JSON.stringify({ userId }),
  })) as TeamJoinOutcome;
}

/** No body: the subject is always the caller. A `userId` here would be a way to
 *  request on somebody else's behalf, which is what the consent rule prevents. */
export async function requestToJoinTeam(
  teamId: string,
): Promise<TeamJoinOutcome> {
  return (await request(`${BASE}/${encodeURIComponent(teamId)}/join-requests`, {
    method: "POST",
  })) as TeamJoinOutcome;
}

export async function leaveTeam(teamId: string): Promise<void> {
  await request(`${BASE}/${encodeURIComponent(teamId)}/leave`, {
    method: "POST",
  });
}

export async function setTeamMemberRole(
  teamId: string,
  userId: string,
  input: { role: TeamMemberRole; version: number },
): Promise<{ version: number }> {
  return (await request(
    `${BASE}/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
    { method: "PATCH", body: JSON.stringify(input) },
  )) as { version: number };
}

export async function removeTeamMember(
  teamId: string,
  userId: string,
  version: number,
): Promise<{ version: number }> {
  return (await request(
    `${BASE}/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}?version=${version}`,
    { method: "DELETE" },
  )) as { version: number };
}

export async function listMyTeamRequests(
  options: { status?: TeamRequestStatus; cursor?: string | null } = {},
): Promise<TeamRequestPage> {
  const params = new URLSearchParams();
  if (options.status) params.set("status", options.status);
  if (options.cursor) params.set("cursor", options.cursor);
  const query = params.toString();
  const path = `${BASE}/requests/mine`;
  return (await request(query ? `${path}?${query}` : path)) as TeamRequestPage;
}

export async function listIncomingTeamRequests(
  options: { teamId?: string; cursor?: string | null } = {},
): Promise<TeamRequestPage> {
  const params = new URLSearchParams();
  if (options.teamId) params.set("teamId", options.teamId);
  if (options.cursor) params.set("cursor", options.cursor);
  const query = params.toString();
  const path = `${BASE}/requests/incoming`;
  return (await request(query ? `${path}?${query}` : path)) as TeamRequestPage;
}

export async function resolveTeamRequest(
  requestId: string,
  action: TeamRequestAction,
): Promise<TeamRequestOutcome> {
  return (await request(
    `${BASE}/requests/${encodeURIComponent(requestId)}/${action}`,
    { method: "POST" },
  )) as TeamRequestOutcome;
}

/** Tenant-admin only. Promotes an EXISTING member; it cannot create one. */
export async function recoverTeamOwnership(
  teamId: string,
  input: { userId: string; reason: string },
): Promise<void> {
  await request(`${ADMIN_BASE}/${encodeURIComponent(teamId)}/ownership`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * Tenant-admin only, and ONLY for a team with no active owner left.
 *
 * The exit from the one state ownership recovery cannot reach: when every
 * member has left the workspace there is nobody to promote, and adding the
 * administrator to the team would break the consent rule. The server refuses
 * with 409 while any active owner remains, so this is a way out of a stuck
 * team rather than a power over a healthy one.
 */
export async function archiveOwnerlessTeam(
  teamId: string,
  input: { reason: string },
): Promise<void> {
  await request(`${ADMIN_BASE}/${encodeURIComponent(teamId)}/archive`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...csrfHeaders(),
      ...init.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      code?: string;
      message?: string;
    } | null;
    /* The SERVER's message when there is one. These are the messages worth
       showing verbatim — "A team keeps at least one owner", "This team changed
       somewhere else" — because each names what to do next, and a generic
       client-side string would throw that away. */
    throw new AdminApiError(
      body?.message ?? body?.code ?? `Request failed (${response.status})`,
      body?.code,
    );
  }
  return response.status === 204 ? null : response.json();
}
